import React, { useContext } from "react";
import IOrder from "../../../models/IOrder";
import IOrder_Progress_State from "../../../models/IOrder_Progress_State";
import OrderSourceEnum from "../../../models/OrderSourceEnum";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import OrderEntryService from "../../../services/OrderEntryService";
import { OrderProgressTrackerItemShipped } from "../OrderProgressTrackerItemShipped/OrderProgressTrackerItemShipped";
import OwLabel from "../OwLabel/OwLabel";
import "./OrderProgressTracker.scss";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UIService from "../../../services/UIService";

type Props = {
    orderStateId: OrderStatesEnum | null;
    order: Partial<IOrder>;
};

export const OrderProgressTracker = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const getCurrentWorkflowStepIndex = () => {
        let index = 0;
        let workflowStates: IOrder_Progress_State[] = supplierModel.order_progress_states || [];

        if (props.order.Workflow) {
            workflowStates = props.order.Workflow.detail;
        }

        // When order is partially_shipped, treat it as shipped for highlighting purposes
        let stateIdToFind = props.orderStateId;
        if (props.orderStateId === OrderStatesEnum.PARTIALLY_SHIPPED) {
            stateIdToFind = OrderStatesEnum.SHIPPED;
        }

        for (let i = 0; i < workflowStates.length; i++) {
            if (workflowStates[i].order_state_id === stateIdToFind) {
                index = i;
                break;
            }
        }

        return index;
    };

    const currentStepIndex = getCurrentWorkflowStepIndex();

    const shouldShowShipments = (): boolean => {
        if (supplierModel.has_shipments) {
            switch (props.order.order_source_id) {
                case OrderSourceEnum.ORDERWERKS:
                case OrderSourceEnum.ORDERWERKS_CART:
                case OrderSourceEnum.APP:
                case OrderSourceEnum.WAYFAIR:
                    return true;

                default:
                    return false;
            }
        } else {
            return false;
        }
    };

    const showShipments = shouldShowShipments();

    // static defaultProps = {
    //     orderStateId: 0,
    // };
    //
    // readonly state: State = {
    //     supplierModel: SupplierService.getLocalSupplierDataFn(),
    // };

    const getChevron = (isSelected?: boolean): any => {
        return (
            <div className={"line " + (isSelected ? "selected" : "")}>
                <i className={"fas fa-chevron-double-right"} />
            </div>
        );
    };

    const getStep = (title: string): IOrder_Progress_State | undefined => {
        const orderSteps = OrderEntryService.getOrderSteps(supplierModel, props.orderStateId, props.order.Workflow?.detail);

        return orderSteps.find((step) => step.name === title);
    };

    const hasNoteOnStepFn = (title: string): boolean => {
        let hasNote = false;

        const step = getStep(title);

        if (step) {
            const stateLogs = props.order.Order_State_Logs;

            if (stateLogs) {
                for (const stateLog of stateLogs) {
                    if (stateLog.order_state_id === step.order_state_id) {
                        if (stateLog.note?.length) {
                            hasNote = true;
                        }

                        if (stateLog.internal_note?.length && userModel.isSupplierUser) {
                            hasNote = true;
                        }
                    }
                }
            }
        }

        return hasNote;
    };

    const getStepMarker = (title: string, isSelected?: boolean, showTitleOnMobile?: boolean): any => {
        const hasNoteOnStep = hasNoteOnStepFn(title);

        return (
            <div
                data-testid={`progress-${title}`}
                id={`progress-${title}`}
                className={"d-flex flex-column " + (isSelected ? "selected" : "")}>
                <div className={"d-flex justify-content-center mb-2"}>
                    <div className={isSelected ? "fas fa-circle-check" : "fas fa-circle"} />
                </div>
                <div className={"font-small text-muted text-truncate-mobile step-title " + (showTitleOnMobile ? "" : "")}>
                    <OwLabel noBottomMargin={true} title={title} supplierModel={supplierModel} isSupplierVendorOverride={true} />
                    {hasNoteOnStep ? <i className={"ms-2 fad fa-sticky-note"} title={"Has Timeline Note"} /> : null}
                </div>
            </div>
        );
    };

    if (props.order.is_cancelled) {
        return null;
    }

    const orderSteps = OrderEntryService.getOrderSteps(supplierModel, props.orderStateId, props.order.Workflow?.detail);

    const currentStep = props.orderStateId;
    let currentStepNo = 0;

    for (const orderStep of orderSteps) {
        if (orderStep.order_state_id === currentStep) {
            currentStepNo = orderStep.step_no;
        }
    }

    const { mainWorkflow, fulfillmentWorkflow, finalStep } = OrderEntryService.segmentOrderStates(orderSteps);

    const isMainViewLight = UIService.isMainViewLight();

    return (
        <div className="OrderProgressTracker">
            <div className={"d-flex flex-row rounded-5 d-inline-flex p-2 " + (isMainViewLight ? "bg-white" : "bg-dark")}>
                <div className={"mainWorkflow d-flex"}>
                    {mainWorkflow.map((step, index) => {
                        const isSelected = index <= currentStepIndex;

                        if (index > 0) {
                            switch (step.order_state_id) {
                                case OrderStatesEnum.SHIPPED:
                                    return (
                                        <OrderProgressTrackerItemShipped
                                            supplierModel={supplierModel}
                                            order={props.order}
                                            getChevron={getChevron}
                                            getStepMarker={getStepMarker}
                                        />
                                    );

                                case OrderStatesEnum.DELIVERED:
                                case OrderStatesEnum.READY_FOR_DELIVERY:
                                case OrderStatesEnum.PICKED_UP:
                                case OrderStatesEnum.READY_FOR_PICKUP:
                                    return null;

                                default: {
                                    return (
                                        <React.Fragment key={index}>
                                            {getChevron(isSelected)}
                                            {getStepMarker(step.name, isSelected, true)}
                                        </React.Fragment>
                                    );
                                }
                            }
                        } else {
                            return <React.Fragment key={index}>{getStepMarker(step.name, isSelected, true)}</React.Fragment>;
                        }
                    })}
                </div>
                {showShipments ? (
                    <div className={"fulfillmentWorkflow"}>
                        <OrderProgressTrackerItemShipped
                            supplierModel={supplierModel}
                            order={props.order}
                            getChevron={getChevron}
                            getStepMarker={getStepMarker}
                        />
                    </div>
                ) : null}
                {finalStep ? (
                    <React.Fragment>
                        {getChevron(props.order.order_state_id === finalStep.order_state_id)}
                        {getStepMarker(finalStep.name, props.order.order_state_id === finalStep.order_state_id, true)}
                    </React.Fragment>
                ) : null}
            </div>
        </div>
    );
};

export default OrderProgressTracker;
